import { trimStr } from './cfoFormat'
import type { CfoPageId, CfoPageRoute } from './cfoNav'

export type CfoPageSlug =
  | 'ceo'
  | 'ceo-analysis'
  | 'home'
  | 'financials'
  | 'close'
  | 'job-costing'
  | 'ratios'
  | 'cash'
  | 'ar'
  | 'forecast'
  | 'scenarios'
  | 'valuation'
  | 'presentations'
  | 'explore'
  | 'gl-detail'
  | 'analysis-profile'
  | 'settings'

export type CfoRouteState = {
  siteSlug: string
  workbookId: string
  page: CfoPageSlug
  canonicalPath: string
  needsReplace: boolean
}

export const CFO_ROUTE_ORIGIN_PARAM = 'fg_origin'
export const CFO_ROUTE_FOCUS_METRIC_PARAM = 'fg_focus_metric'
export const CFO_ROUTE_FOCUS_TEXT_PARAM = 'fg_focus_text'

export const CFO_LIGHTWEIGHT_PAGE_IDS: readonly CfoPageSlug[] = [
  'ceo',
  'ceo-analysis',
  'home',
  'financials',
  'close',
  'job-costing',
  'ratios',
  'ar',
  'cash',
  'forecast',
  'scenarios',
  'valuation',
  'presentations',
  'explore',
  'gl-detail',
  'analysis-profile',
  'settings',
]

const CFO_LIGHTWEIGHT_PAGE_SET = new Set<string>(CFO_LIGHTWEIGHT_PAGE_IDS)

// Pages that need the in-browser app worker booted. `gl-detail` needs it for
// DuckDB SQL; `financials` and `ar` need it so the spreadsheet worker can build
// the Excel export snapshot (`export_excel_snapshot`) — without it the download
// buttons on those pages are permanently disabled in the lightweight CFO Core
// surface, which is not what we want.
export const CFO_WORKER_REQUIRED_PAGE_IDS: readonly CfoPageSlug[] = [
  'gl-detail',
  'financials',
  'ar',
  // CEO Analysis runs its own dedicated DuckDB worker, but its XLSX export
  // path ships through the app worker's `export_excel_snapshot`.
  'ceo-analysis',
]

const CFO_WORKER_REQUIRED_PAGE_SET = new Set<string>(CFO_WORKER_REQUIRED_PAGE_IDS)

const decodeRouteSegment = (value: string): string => {
  try {
    return decodeURIComponent(String(value || ''))
  } catch {
    return String(value || '')
  }
}

const encodeRouteSegment = (value: string): string => encodeURIComponent(String(value || '').trim())

export const isCfoPageSlug = (value: unknown): value is CfoPageSlug =>
  typeof value === 'string' && CFO_LIGHTWEIGHT_PAGE_SET.has(value)

const normalizeCfoPageSlug = (value: string): CfoPageSlug | null => {
  const normalized = trimStr(value).toLowerCase()
  if (normalized === 'gl_detail') return 'gl-detail'
  if (normalized === 'ceo_analysis') return 'ceo-analysis'
  if (normalized === 'job_costing') return 'job-costing'
  if (normalized === 'analysis_profile') return 'analysis-profile'
  if (normalized === 'ai-tuning') return 'analysis-profile'
  if (normalized === 'ceo-cockpit' || normalized === 'cockpit' || normalized === 'ceo_cockpit') return 'ceo'
  return isCfoPageSlug(normalized) ? normalized : null
}

export const cfoRouteFromPage = (page: CfoPageSlug): CfoPageRoute => ({
  pageId: (
    page === 'job-costing'
      ? 'job_costing'
      : page === 'gl-detail'
      ? 'gl_detail'
      : page === 'ceo-analysis'
        ? 'ceo_analysis'
        : page === 'analysis-profile'
          ? 'analysis_profile'
          : page === 'settings'
            ? 'advanced'
            : page
  ) as CfoPageId,
  view: page === 'ratios' ? 'ratios' : page === 'valuation' ? 'valuation' : page === 'settings' ? 'settings' : null,
})

export const cfoPageFromRoute = (route: CfoPageRoute | null | undefined): CfoPageSlug => {
  const pageId = String(route?.pageId || '').trim()
  if (pageId === 'gl_detail') return 'gl-detail'
  if (pageId === 'ceo_analysis') return 'ceo-analysis'
  if (pageId === 'job_costing') return 'job-costing'
  if (pageId === 'analysis_profile') return 'analysis-profile'
  if (pageId === 'advanced' && route?.view === 'settings') return 'settings'
  return isCfoPageSlug(pageId) ? pageId : 'home'
}

export const buildCfoRoutePath = (args: {
  siteSlug: string
  workbookId: string
  page?: CfoPageSlug | null
}): string => {
  const siteSlug = encodeRouteSegment(args.siteSlug)
  const workbookId = encodeRouteSegment(args.workbookId)
  const page = args.page ? normalizeCfoPageSlug(args.page) || 'home' : 'home'
  if (!siteSlug || !workbookId) return '/'
  return `/${siteSlug}/workbooks/${workbookId}/cfo/${page}`
}

/** Firm Cockpit route for a firm (Site): /<siteSlug>/firm. */
export const buildFirmCockpitPath = (siteSlug: string): string => {
  const slug = encodeRouteSegment(siteSlug)
  return slug ? `/${slug}/firm` : '/'
}

/** Add the durable firm-cockpit return context plus an optional exact metric focus. */
export const withFirmCockpitContext = (
  path: string,
  focus?: { metricId?: string | null; text?: string | null },
): string => {
  const params = new URLSearchParams()
  params.set(CFO_ROUTE_ORIGIN_PARAM, 'firm')
  const metricId = trimStr(focus?.metricId)
  const text = trimStr(focus?.text)
  if (metricId) params.set(CFO_ROUTE_FOCUS_METRIC_PARAM, metricId)
  if (text) params.set(CFO_ROUTE_FOCUS_TEXT_PARAM, text)
  return `${path}?${params.toString()}`
}

export const cfoFirmOriginSearch = (
  loc: Pick<Location, 'search'> | null | undefined,
): string => {
  try {
    const params = new URLSearchParams(String(loc?.search || ''))
    return params.get(CFO_ROUTE_ORIGIN_PARAM) === 'firm'
      ? `?${CFO_ROUTE_ORIGIN_PARAM}=firm`
      : ''
  } catch {
    return ''
  }
}

export const cfoMetricFocusFromLocation = (
  loc: Pick<Location, 'search'> | null | undefined,
): { metricId: string; text: string } => {
  try {
    const params = new URLSearchParams(String(loc?.search || ''))
    return {
      metricId: trimStr(params.get(CFO_ROUTE_FOCUS_METRIC_PARAM)),
      text: trimStr(params.get(CFO_ROUTE_FOCUS_TEXT_PARAM)),
    }
  } catch {
    return { metricId: '', text: '' }
  }
}

/** Backward-compatible name for firm-cockpit callers; focus is route-generic. */
export const cfoFirmFocusFromLocation = cfoMetricFocusFromLocation

export const parseCfoRouteState = (
  loc: Pick<Location, 'pathname' | 'search'> | null | undefined,
  opts?: { defaultPage?: CfoPageSlug },
): CfoRouteState | null => {
  const pathname = String(loc?.pathname || '')
  const parts = pathname.split('/').map((part) => part.trim()).filter(Boolean)
  if (parts.length < 4 || parts[1] !== 'workbooks' || (parts[3] !== 'cfo' && parts[3] !== 'cfo-home')) return null
  const siteSlug = decodeRouteSegment(parts[0] || '').trim()
  const workbookId = decodeRouteSegment(parts[2] || '').trim()
  if (!siteSlug || !workbookId) return null
  const params = (() => {
    try {
      return new URLSearchParams(String(loc?.search || ''))
    } catch {
      return new URLSearchParams()
    }
  })()
  const legacySection = trimStr(params.get('fg_cfo_section') || params.get('cfo_section') || params.get('cfoSection')).toLowerCase()
  const legacyView = trimStr(params.get('fg_cfo_view') || params.get('cfo_view') || params.get('cfoView')).toLowerCase()
  const legacyPage = legacySection === 'advanced' && legacyView === 'settings' ? 'settings' : legacySection
  const rawPage = trimStr(parts[3] === 'cfo-home' ? legacyPage : parts[4] || '').toLowerCase()
  const page = normalizeCfoPageSlug(rawPage) || opts?.defaultPage || 'home'
  const canonicalPath = buildCfoRoutePath({ siteSlug, workbookId, page })
  const normalizedPath = pathname.replace(/\/+$/g, '') || '/'
  return {
    siteSlug,
    workbookId,
    page,
    canonicalPath,
    needsReplace: normalizedPath !== canonicalPath,
  }
}

export const cfoRouteNeedsAppWorker = (
  loc: Pick<Location, 'pathname' | 'search'> | null | undefined,
): boolean => {
  const route = parseCfoRouteState(loc)
  return Boolean(route && CFO_WORKER_REQUIRED_PAGE_SET.has(route.page))
}
